import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Grid, Typography, CircularProgress, Button, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, MenuItem, Box, IconButton, Tooltip, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import GrassIcon from '@mui/icons-material/Grass';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';
import { getPlantSuggestions, searchPlantGuides } from '../../services/plantAI';
import PlantCard from '../Dashboard/PlantCard';
import { STAGE_LABELS } from '../../utils/labels';

const STAGES = ['seedling', 'vegetative', 'flowering', 'harvested'];
const DEVICE_ID = 'esp32_001';

export default function PlantList() {
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [loading, setLoading] = useState(true);

    // Diálogos
    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [finishOpen, setFinishOpen] = useState(false);
    const [finishId, setFinishId] = useState(null);
    const [dryOpen, setDryOpen] = useState(false);
    const [dryId, setDryId] = useState(null);
    const [harvestOpen, setHarvestOpen] = useState(false);
    const [harvestId, setHarvestId] = useState(null);

    const [form, setForm] = useState({
        name: '', strain: '', growthStage: 'seedling', wateringAmount: 100, notes: '', startDate: '', vasoL: '', guideKey: '', guideMode: '', environment: 'indoor'
    });
    const [editForm, setEditForm] = useState({});
    const [guideOptions, setGuideOptions] = useState([]);
    const [guideSearch, setGuideSearch] = useState('');
    const [guideResults, setGuideResults] = useState([]);
    const [guideLoading, setGuideLoading] = useState(false);
    const [finishType, setFinishType] = useState('harvested');
    const [wetWeight, setWetWeight] = useState('');
    const [dryWeight, setDryWeight] = useState('');

    const load = () => {
        api.get(`/plants?deviceId=${DEVICE_ID}`).then(res => { setPlants(res.data); setLoading(false); })
            .catch(() => setLoading(false));
    };

    useEffect(load, []);

    // Carrega as plantas do guia para o seletor de "tipo de cultivo"
    useEffect(() => {
        getPlantSuggestions().then(setGuideOptions).catch(() => setGuideOptions([]));
    }, []);

    // Busca digitável do guia de cultivo (autocomplete)
    const handleGuideSearch = async (term) => {
        setGuideSearch(term);
        if (!term.trim()) { setGuideResults([]); return; }
        setGuideLoading(true);
        try {
            const res = await searchPlantGuides(term);
            setGuideResults(res);
        } catch { setGuideResults([]); }
        finally { setGuideLoading(false); }
    };
    const selectGuide = (g) => {
        setGuideSearch(g.nome);
        setGuideResults([]);
        setForm({ ...form, guideKey: g.key, guideMode: g.modo ? 'fotoperiodo' : '' });
    };

    // ---------- Adicionar ----------
    const handleAdd = async () => {
        if (!form.name.trim()) { toast.error('Informe o nome da planta'); return; }
        try {
            const payload = { ...form, deviceId: DEVICE_ID };
            if (form.startDate) payload.startDate = new Date(form.startDate).toISOString();
            await api.post('/plants', payload);
            toast.success('Planta adicionada');
            setAddOpen(false);
            setForm({ name: '', strain: '', growthStage: 'seedling', wateringAmount: 100, notes: '', startDate: '', vasoL: '', guideKey: '', guideMode: '', environment: 'indoor' });
            setGuideSearch(''); setGuideResults([]);
            load();
        } catch (e) { toast.error('Erro ao adicionar planta'); }
    };

    // ---------- Editar ----------
    const openEdit = (p) => {
        setEditId(p._id || p.id);
        setEditForm({
            name: p.name, strain: p.strain || '', growthStage: p.growthStage,
            wateringAmount: p.wateringAmount || 100, notes: p.notes || '',
            startDate: p.startDate ? new Date(p.startDate).toISOString().slice(0, 10) : '',
            vasoL: p.vasoL || '', guideKey: p.guideKey || '', guideMode: p.guideMode || '', environment: p.environment || 'indoor'
        });
        setGuideSearch(p.guideKey ? (p.guideKey) : '');
        setGuideResults([]);
        setEditOpen(true);
    };

    const handleEdit = async () => {
        if (!editForm.name.trim()) { toast.error('Informe o nome da planta'); return; }
        try {
            const payload = { ...editForm };
            if (editForm.startDate) payload.startDate = new Date(editForm.startDate).toISOString();
            await api.put(`/plants/${editId}`, payload);
            toast.success('Planta atualizada');
            setEditOpen(false);
            load();
        } catch (e) { toast.error('Erro ao atualizar planta'); }
    };

    // ---------- Colher (peso úmido) ----------
    const openHarvest = (id) => { setHarvestId(id); setWetWeight(''); setHarvestOpen(true); };

    const handleHarvest = async () => {
        try {
            const payload = { endType: 'harvested' };
            if (wetWeight) payload.wetWeight = Number(wetWeight);
            await api.post(`/plants/${harvestId}/finish`, payload);
            toast.success('Planta colhida!');
            setHarvestOpen(false);
            load();
        } catch (e) { toast.error('Erro ao colher planta'); }
    };

    // ---------- Matar (usado dentro do Editar) ----------
    const openFinish = (id) => { setFinishId(id); setFinishType('killed'); setFinishOpen(true); };

    const handleFinish = async () => {
        try {
            const payload = { endType: finishType };
            await api.post(`/plants/${finishId}/finish`, payload);
            toast.success(finishType === 'killed' ? 'Planta finalizada (morta)' : 'Planta finalizada');
            setFinishOpen(false);
            load();
        } catch (e) { toast.error('Erro ao finalizar planta'); }
    };

    // ---------- Peso seco (após 30 dias) ----------
    const openDry = (id) => { setDryId(id); setDryWeight(''); setDryOpen(true); };
    const handleDry = async () => {
        try {
            await api.post(`/plants/${dryId}/dry-weight`, { dryWeight: Number(dryWeight) });
            toast.success('Peso seco registrado');
            setDryOpen(false);
            load();
        } catch (e) { toast.error('Erro ao registrar peso seco'); }
    };

    // ---------- Remover ----------
    const handleDelete = async (id) => {
        if (!window.confirm('Remover esta planta?')) return;
        try {
            await api.delete(`/plants/${id}`);
            toast.success('Planta removida');
            load();
        } catch (e) { toast.error('Erro ao remover'); }
    };

    if (loading) return <CircularProgress sx={{ m: 4 }} />;

    const active = plants.filter(p => p.status !== 'archived');
    const archived = plants.filter(p => p.status === 'archived');

    return (
        <Grid container spacing={2} sx={{ p: 3 }}>
            <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4">🌿 Plantas</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                    Nova Planta
                </Button>
            </Grid>

            {active.length === 0 && archived.length === 0 && (
                <Grid item xs={12}><Typography color="textSecondary">Nenhuma planta cadastrada.</Typography></Grid>
            )}

            {active.map((p) => {
                const id = p._id || p.id;
                return (
                    <Grid item xs={12} sm={6} md={3} key={id}>
                        <Box onClick={() => navigate(`/plants/${id}`)} sx={{ cursor: 'pointer' }}>
                            <PlantCard plant={p} />
                        </Box>
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Tooltip title="Editar">
                                <IconButton size="small" color="info" onClick={() => openEdit(p)}>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Colher">
                                <IconButton size="small" color="success" onClick={() => openHarvest(id)}>
                                    <GrassIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Remover">
                                <IconButton size="small" color="error" onClick={() => handleDelete(id)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Grid>
                );
            })}

            {archived.length > 0 && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button variant="outlined" onClick={() => navigate('/history')}>
                        📜 Ver histórico ({archived.length})
                    </Button>
                </Grid>
            )}

            {/* Diálogo: Nova Planta */}
            <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Nova Planta</DialogTitle>
                <DialogContent>
                    <TextField fullWidth margin="dense" label="Buscar guia de cultivo (digite para pesquisar)"
                        value={guideSearch}
                        onChange={e => handleGuideSearch(e.target.value)}
                        placeholder="Ex.: tomate, cannabis, alface..."
                        InputProps={{ endAdornment: guideLoading ? <CircularProgress size={18} /> : null }}
                    />
                    {guideResults.length > 0 && (
                        <Box sx={{ maxHeight: 160, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1, mb: 1 }}>
                            {guideResults.map(g => (
                                <Box key={g.key} sx={{ p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(76,175,80,0.12)' } }}
                                    onClick={() => selectGuide(g)}>
                                    {g.nome}{g.modo ? ' (fotoperíodo/automática)' : ''}
                                </Box>
                            ))}
                        </Box>
                    )}
                    {form.guideKey && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            Guia selecionado: <b>{form.guideKey}</b>. Configuração automática de luz, rega, umidade e ventilação será aplicada.
                        </Alert>
                    )}
                    {form.guideKey && (
                        <TextField select fullWidth margin="dense" label="Modo (se aplicável)"
                            value={form.guideMode || ''}
                            onChange={e => setForm({ ...form, guideMode: e.target.value })}>
                            <MenuItem value="">— Padrão —</MenuItem>
                            <MenuItem value="fotoperiodo">Fotoperíodo</MenuItem>
                            <MenuItem value="automatica">Automática</MenuItem>
                        </TextField>
                    )}
                    <TextField select fullWidth margin="dense" label="Ambiente"
                        value={form.environment || 'indoor'}
                        onChange={e => setForm({ ...form, environment: e.target.value })}>
                        <MenuItem value="indoor">Indoor (estufa/controle)</MenuItem>
                        <MenuItem value="outdoor">Outdoor (sol natural)</MenuItem>
                    </TextField>
                    <TextField fullWidth margin="dense" label="Nome" value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })} />
                    <TextField fullWidth margin="dense" label="Strain" value={form.strain}
                        onChange={e => setForm({ ...form, strain: e.target.value })} />
                    <TextField select fullWidth margin="dense" label="Estágio" value={form.growthStage}
                        onChange={e => setForm({ ...form, growthStage: e.target.value })}>
                        {STAGES.map(s => <MenuItem key={s} value={s}>{STAGE_LABELS[s]}</MenuItem>)}
                    </TextField>
                    <TextField fullWidth margin="dense" type="date" label="Início (dias de vida já passados)"
                        InputLabelProps={{ shrink: true }} value={form.startDate}
                        onChange={e => setForm({ ...form, startDate: e.target.value })} />
                    <TextField fullWidth margin="dense" type="number" label="Quantidade de rega (ml)" value={form.wateringAmount}
                        onChange={e => setForm({ ...form, wateringAmount: Number(e.target.value) })} />
                    <TextField fullWidth margin="dense" type="number" label="Tamanho do vaso (L)"
                        value={form.vasoL} onChange={e => setForm({ ...form, vasoL: Number(e.target.value) })} />
                    <TextField fullWidth margin="dense" multiline minRows={2} label="Notas" value={form.notes}
                        onChange={e => setForm({ ...form, notes: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleAdd}>Adicionar</Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo: Editar Planta */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Editar Planta</DialogTitle>
                <DialogContent>
                    <TextField fullWidth margin="dense" label="Nome" value={editForm.name || ''}
                        onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                    <TextField fullWidth margin="dense" label="Strain" value={editForm.strain || ''}
                        onChange={e => setEditForm({ ...editForm, strain: e.target.value })} />
                    <TextField select fullWidth margin="dense" label="Estágio (ciclo)" value={editForm.growthStage || 'seedling'}
                        onChange={e => setEditForm({ ...editForm, growthStage: e.target.value })}>
                        {STAGES.map(s => <MenuItem key={s} value={s}>{STAGE_LABELS[s]}</MenuItem>)}
                    </TextField>
                    <TextField fullWidth margin="dense" type="date" label="Início (dias de vida já passados)"
                        InputLabelProps={{ shrink: true }} value={editForm.startDate || ''}
                        onChange={e => setEditForm({ ...editForm, startDate: e.target.value })} />
                    <TextField fullWidth margin="dense" type="number" label="Quantidade de rega (ml)" value={editForm.wateringAmount || 100}
                        onChange={e => setEditForm({ ...editForm, wateringAmount: Number(e.target.value) })} />
                    <TextField fullWidth margin="dense" label="Buscar guia de cultivo (digite para pesquisar)"
                        value={guideSearch}
                        onChange={e => handleGuideSearch(e.target.value)}
                        placeholder="Ex.: tomate, cannabis, alface..."
                        InputProps={{ endAdornment: guideLoading ? <CircularProgress size={18} /> : null }}
                    />
                    {guideResults.length > 0 && (
                        <Box sx={{ maxHeight: 160, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1, mb: 1 }}>
                            {guideResults.map(g => (
                                <Box key={g.key} sx={{ p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(76,175,80,0.12)' } }}
                                    onClick={() => { selectGuide(g); setEditForm({ ...editForm, guideKey: g.key, guideMode: g.modo ? 'fotoperiodo' : '' }); }}>
                                    {g.nome}{g.modo ? ' (fotoperíodo/automática)' : ''}
                                </Box>
                            ))}
                        </Box>
                    )}
                    {editForm.guideKey && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            Guia selecionado: <b>{editForm.guideKey}</b>. Configuração automática será reaplicada.
                        </Alert>
                    )}
                    {editForm.guideKey && (
                        <TextField select fullWidth margin="dense" label="Modo (se aplicável)"
                            value={editForm.guideMode || ''}
                            onChange={e => setEditForm({ ...editForm, guideMode: e.target.value })}>
                            <MenuItem value="">— Padrão —</MenuItem>
                            <MenuItem value="fotoperiodo">Fotoperíodo</MenuItem>
                            <MenuItem value="automatica">Automática</MenuItem>
                        </TextField>
                    )}
                    <TextField select fullWidth margin="dense" label="Ambiente"
                        value={editForm.environment || 'indoor'}
                        onChange={e => setEditForm({ ...editForm, environment: e.target.value })}>
                        <MenuItem value="indoor">Indoor (estufa/controle)</MenuItem>
                        <MenuItem value="outdoor">Outdoor (sol natural)</MenuItem>
                    </TextField>
                    <TextField fullWidth margin="dense" type="number" label="Tamanho do vaso (L) — transplante"
                        value={editForm.vasoL || ''} onChange={e => setEditForm({ ...editForm, vasoL: Number(e.target.value) })} />
                    <TextField fullWidth margin="dense" multiline minRows={2} label="Notas" value={editForm.notes || ''}
                        onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
                    {editId && (
                        <Button color="error" onClick={() => { setEditOpen(false); openFinish(editId); }}>
                            💀 Matar
                        </Button>
                    )}
                    <Button variant="contained" onClick={handleEdit}>Salvar</Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo: Matar (usado dentro do Editar) */}
            <Dialog open={finishOpen} onClose={() => setFinishOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Matar Planta</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary">
                        A planta será arquivada sem registro de colheita.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFinishOpen(false)}>Cancelar</Button>
                    <Button variant="contained" color="error" onClick={handleFinish}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo: Colher (peso úmido) */}
            <Dialog open={harvestOpen} onClose={() => setHarvestOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Colher Planta</DialogTitle>
                <DialogContent>
                    <TextField fullWidth margin="dense" type="number" label="Peso úmido da colheita (g)"
                        value={wetWeight} onChange={e => setWetWeight(e.target.value)} />
                    <Typography variant="caption" color="textSecondary">
                        Após ~30 dias de secagem, registre o peso seco pelo ícone de folha.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHarvestOpen(false)}>Cancelar</Button>
                    <Button variant="contained" color="success" onClick={handleHarvest}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo: Peso Seco */}
            <Dialog open={dryOpen} onClose={() => setDryOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Peso Seco (após 30 dias)</DialogTitle>
                <DialogContent>
                    <TextField fullWidth margin="dense" type="number" label="Peso seco (g)"
                        value={dryWeight} onChange={e => setDryWeight(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDryOpen(false)}>Cancelar</Button>
                    <Button variant="contained" color="success" onClick={handleDry}>Registrar</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}
